import uiI18n from "../i18n";
import { useTranslation as useUiTranslation } from "react-i18next";
import { useFavorite } from "../context/EngagementContext";
import { vacationTypeLabel } from "../utils/vacationTypes";
import { addReservation, calendarDate, reservationEstimate, stayNights } from "../Services/reservationService";
import React, {
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import DatePicker from "react-datepicker";
import { fr, enGB, arTN } from "date-fns/locale";

import {
  LuArrowRight,
  LuBath,
  LuBedDouble,
  LuCalendarDays,
  LuCheck,
   LuLoaderCircle,
  LuHeart,
  LuImage,
  LuMapPin,
  LuMessageSquare,
  LuPencil,
  LuPhone,
  LuSend,
  LuTag,
  LuTrash2,
  LuUsers,
  LuX,
} from "react-icons/lu";

import {
  getReservedDates,
  getVacationById,
} from "../Services/vacationsService";

import { photoUrl } from "../Variables.jsx";

import "react-datepicker/dist/react-datepicker.css";

const FALLBACK_IMAGE =
  "https://placehold.co/900x560/e2e8f0/64748b?text=Lina+Immo+Vacances";

const getDefaultMessage = () => uiI18n.t("ui.vacation_message");

function ListVacation({
  listing,
  onEdit,
  onDelete,
  isHome = false,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const auth = getAuth();

  const listingId =
    listing?.id ??
    listing?.ID ??
    listing?.vacationID;

  const { isFavorite, toggleFavorite, favoriteLoading } = useFavorite("vacation", listing);

  const [imageLoaded, setImageLoaded] =
    useState(false);

  const [imageError, setImageError] =
    useState(false);

  const [showPhone, setShowPhone] =
    useState(false);

  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const [vacationDetails, setVacationDetails] =
    useState(null);

  const [loadingDetails, setLoadingDetails] =
    useState(false);

  const [reservedDates, setReservedDates] =
    useState([]);

  const [startDate, setStartDate] =
    useState(null);

  const [endDate, setEndDate] =
    useState(null);

  const [travelers, setTravelers] =
    useState(1);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState(
    getDefaultMessage()
  );

  const [emailError, setEmailError] =
    useState("");

  const [isSending, setIsSending] =
    useState(false);

  const images = useMemo(() => {
    if (!listing?.urlImages) {
      return [];
    }

    const rawImages = Array.isArray(
      listing.urlImages
    )
      ? listing.urlImages
      : String(listing.urlImages).split(",");

    return rawImages
      .map((image) => String(image).trim())
      .filter(Boolean)
      .map((image) => {
        if (
          image.startsWith("http://") ||
          image.startsWith("https://") ||
          image.startsWith("data:")
        ) {
          return image;
        }

        return photoUrl(image, listing?.folder);
      });
  }, [listing?.folder, listing?.urlImages]);

  const imageUrl =
    imageError || images.length === 0
      ? FALLBACK_IMAGE
      : images[0];

  const normalPrice =
    Number(listing?.prix) || 0;

  const reducedPrice =
    Number(listing?.prixReduit) || 0;

  const hasOffer =
    Boolean(listing?.offre) &&
    reducedPrice > 0 &&
    reducedPrice < normalPrice;

  const nightlyPrice = hasOffer
    ? reducedPrice
    : normalPrice;

  const discountPercentage =
    hasOffer && normalPrice > 0
      ? Math.round(
          ((normalPrice - reducedPrice) /
            normalPrice) *
            100
        )
      : 0;

  const formatPrice = (value) =>
    new Intl.NumberFormat(uiI18n.language, {
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);

  const location = [
    t(`delegations.${listing?.ville}.${listing?.delegation}`, listing?.delegation || ""),
    t(`cities.${listing?.ville}`, listing?.ville || ""),
  ]
    .filter((value) => {
      const normalizedValue = String(
        value || ""
      )
        .trim()
        .toLowerCase();

      return (
        normalizedValue &&
        normalizedValue !== "undefined" &&
        normalizedValue !== "null"
      );
    })
    .join(", ");

  const publicationDate =
    listing?.dateAjout ??
    listing?.dateCreation ??
    listing?.createdAt ??
    null;

  const publicationLabel = useMemo(() => {
    if (!publicationDate) {
      return "";
    }

    const parsedDate = new Date(
      publicationDate
    );

    if (
      Number.isNaN(parsedDate.getTime())
    ) {
      return "";
    }

    const language =
      i18n.resolvedLanguage?.split("-")[0] ||
      i18n.language?.split("-")[0] ||
      "fr";

    const locale =
      language === "ar"
        ? "ar-TN"
        : language === "en"
          ? "en-GB"
          : "fr-FR";

    const difference = Math.max(
      0,
      Date.now() - parsedDate.getTime()
    );

    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    const relativeFormatter =
      new Intl.RelativeTimeFormat(locale, {
        numeric: "always",
      });

    if (difference < hour) {
      return relativeFormatter.format(
        -Math.max(
          1,
          Math.floor(difference / minute)
        ),
        "minute"
      );
    }

    if (difference < day) {
      return relativeFormatter.format(
        -Math.max(
          1,
          Math.floor(difference / hour)
        ),
        "hour"
      );
    }

    if (difference < 30 * day) {
      return relativeFormatter.format(
        -Math.max(
          1,
          Math.floor(difference / day)
        ),
        "day"
      );
    }

    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(parsedDate);
  }, [
    publicationDate,
    i18n.language,
    i18n.resolvedLanguage,
  ]);

  const totalDays = startDate && endDate ? Math.max(0, stayNights(startDate, endDate)) : 0;
  const priceCalculation = reservationEstimate(vacationDetails || listing, totalDays);

  const excludedDateIntervals =
    useMemo(() => {
      return reservedDates
        .map((reservation) => {
          const start = new Date(
            reservation?.startDate ??
              reservation?.arrival ??
              reservation?.Arrival
          );

          const end = new Date(
            reservation?.endDate ??
              reservation?.depart ??
              reservation?.Depart
          );

          if (
            Number.isNaN(start.getTime()) ||
            Number.isNaN(end.getTime())
          ) {
            return null;
          }

          return { start, end };
        })
        .filter(Boolean);
    }, [reservedDates]);

  const isOwner =
    auth.currentUser?.uid &&
    auth.currentUser.uid === listing?.uid;

  const openListing = () => {
    if (!listingId) {
      return;
    }

    // The searched stay (if any) pre-fills the reservation dates on the listing page.
    const params = new URLSearchParams(search);
    const stay = params.get("checkIn") && params.get("checkOut")
      ? "?" + new URLSearchParams({ checkIn: params.get("checkIn"), checkOut: params.get("checkOut") })
      : "";
    navigate(
      `/DetailsVacation/${listingId}${stay}`
    );
  };

  const handleCardKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      openListing();
    }
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const handleFavorite = (event) => {
    stopPropagation(event);

    toggleFavorite();
  };

  const handleEdit = (event) => {
    stopPropagation(event);
    onEdit?.(listing);
  };

  const handleDelete = (event) => {
    stopPropagation(event);
    onDelete?.(listing);
  };

  const handleCall = (event) => {
    stopPropagation(event);

    if (showPhone && listing?.tel) {
      window.location.href = `tel:${listing.tel}`;
      return;
    }

    setShowPhone(true);
  };

  const openReservationModal = (event) => {
    stopPropagation(event);

    setName(
      auth.currentUser?.displayName || ""
    );

    setEmail(
      auth.currentUser?.email || ""
    );

    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen || !listingId) {
      return;
    }

    let componentMounted = true;

    const loadReservationData =
      async () => {
        setLoadingDetails(true);

        try {
          const [
            detailsResponse,
            datesResponse,
          ] = await Promise.allSettled([
            getVacationById(listingId),
            getReservedDates(listingId),
          ]);

          if (!componentMounted) {
            return;
          }

          if (
            detailsResponse.status ===
            "fulfilled"
          ) {
            setVacationDetails(
              detailsResponse.value
            );
          }

          if (
            datesResponse.status ===
            "fulfilled"
          ) {
            setReservedDates(
              Array.isArray(
                datesResponse.value
              )
                ? datesResponse.value
                : []
            );
          }
        } catch (error) {
          console.error(
            "Erreur lors du chargement :",
            error
          );
        } finally {
          if (componentMounted) {
            setLoadingDetails(false);
          }
        }
      };

    loadReservationData();

    return () => {
      componentMounted = false;
    };
  }, [isModalOpen, listingId]);

  useEffect(() => {
    document.body.style.overflow =
      isModalOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  const closeModal = () => {
    setIsModalOpen(false);
    setStartDate(null);
    setEndDate(null);
    setTravelers(1);
    setMessage(getDefaultMessage());
    setEmailError("");
  };

  const validateEmail = (value) =>
    /\S+@\S+\.\S+/.test(value);

  const handleEmailChange = (event) => {
    const value = event.target.value;

    setEmail(value);

    setEmailError(
      value && !validateEmail(value)
        ? t(
            "details_vacation.invalid_email",
            "Veuillez saisir une adresse e-mail valide."
          )
        : ""
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSending) {
      return;
    }

    const currentUser =
      auth.currentUser;

    if (!currentUser) {
      toast.error(
        t(
          "details_vacation.login_required",
          "Veuillez vous connecter pour effectuer une réservation."
        )
      );

      navigate("/SignIn");
      return;
    }

    if (
      !startDate ||
      !endDate ||
      totalDays <= 0
    ) {
      toast.error(
        t(
          "details_vacation.invalid_dates",
          "Sélectionnez des dates valides."
        )
      );

      return;
    }

    setIsSending(true);

    try {
      const details =
        vacationDetails || listing;

      const payload = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),

        Arrival:
          calendarDate(startDate),
        Depart: calendarDate(endDate),

        NbTraveler: Number(travelers),
        TotalDays: totalDays,

        Price:
          Number(
            details?.offre &&
              Number(
                details?.prixReduit
              ) > 0
              ? details.prixReduit
              : details?.prix
          ) || 0,

        Total: priceCalculation.total,
        Discount:
          priceCalculation.discountAmount,

        Libelle: listing?.libelle,
        Ads: window.location.href,

        uidLandlord:
          details?.uid ||
          listing?.uid,

        uidUser: currentUser.uid,

        ContactName:
          currentUser.displayName || name,

        Email:
          currentUser.email || email,

        ProporietsID: listingId,
      };

      const result = await addReservation(payload);

      toast.success(
        (result?.total != null ? uiI18n.t("ui.confirmed_total", { total: result.total }) : "") + t(
          "details_vacation.reservation_sent",
          "Votre demande de réservation a été envoyée."
        )
      );

      closeModal();
    } catch (error) {
      toast.error(
        error?.message ||
          t(
            "details_vacation.email_error",
            "Une erreur est survenue lors de l’envoi."
          )
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <li className="h-full list-none">
        <article
          role="link"
          tabIndex={0}
          onClick={openListing}
          onKeyDown={handleCardKeyDown}
          className="lina-property-card
            group
            flex
            h-full
            cursor-pointer
            flex-col
            overflow-hidden
            rounded-2xl
            border
            border-slate-200
            bg-white
            shadow-sm
            transition-all
            duration-300
            hover:-translate-y-1
            hover:border-green-200
            hover:shadow-xl
            focus:outline-none
            focus:ring-4
            focus:ring-green-500/15
          "
        >
          {/* Image */}
          <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
            {!imageLoaded && (
              <div className="absolute inset-0 animate-pulse bg-slate-200" />
            )}

            <img
              src={imageUrl}
              alt={
                listing?.libelle ||
                t(
                  "vacation.accommodation",
                  "Location de vacances"
                )
              }
              loading="lazy"
              decoding="async"
              onLoad={() =>
                setImageLoaded(true)
              }
              onError={() => {
                setImageError(true);
                setImageLoaded(true);
              }}
              className={`
                h-full
                w-full
                object-cover
                transition-all
                duration-500
                group-hover:scale-105
                ${
                  imageLoaded
                    ? "opacity-100"
                    : "opacity-0"
                }
              `}
            />

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-slate-950/5" />

            {/* Badges */}
            <div className="absolute start-3 top-3 flex max-w-[70%] flex-wrap gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-white shadow-sm">
                <LuTag size={11} />

                {t(
                  "vacances",
                  "Vacances"
                )}
              </span>

              {listing?.category && (
                <span className="rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold text-slate-700 shadow-sm">
                  {vacationTypeLabel(t, listing.category)}
                </span>
              )}

              {hasOffer && (
                <span className="rounded-full bg-red-500 px-2.5 py-1 text-[10px] font-black text-white">
                  -{discountPercentage} %
                </span>
              )}
            </div>

            {/* Favori */}
            <button
  type="button"
  disabled={favoriteLoading}
  onClick={handleFavorite}
  aria-pressed={isFavorite}
  aria-label={
    isFavorite
      ? uiI18n.t("remove_from_favorites")
      : uiI18n.t("add_to_favorites")
  }
  title={
    isFavorite
      ? uiI18n.t("remove_from_favorites")
      : uiI18n.t("add_to_favorites")
  }
  className="
    group
    inline-flex
    h-10
    items-center
    justify-center
    gap-2
    rounded-xl
    border
    border-slate-200
    bg-white
    px-4
    text-sm
    font-semibold
    text-slate-700
    shadow-sm
    transition-all
    duration-200

    hover:border-green-200
    hover:bg-green-50
    hover:text-green-700
    hover:shadow

    focus:outline-none
    focus:ring-2
    focus:ring-green-500/20

    disabled:cursor-not-allowed
    disabled:opacity-60
    disabled:hover:border-slate-200
    disabled:hover:bg-white
    disabled:hover:text-slate-700
    disabled:hover:shadow-sm
  "
>
  {favoriteLoading ? (
    <>
      <LuLoaderCircle
        size={18}
        strokeWidth={2}
        className="animate-spin"
      />

      <span>{uiI18n.t("saving")}</span>
    </>
  ) : (
    <>
      <LuHeart
        size={18}
        strokeWidth={2}
        fill={isFavorite ? "currentColor" : "none"}
        className={`
          transition-transform
          duration-200
          group-hover:scale-110
          ${
            isFavorite
              ? "text-red-500 group-hover:text-green-700"
              : ""
          }
        `}
      />

      <span>
        {isFavorite
          ? uiI18n.t("in_my_favorites")
          : uiI18n.t("add_to_favorites")}
      </span>
    </>
  )}
</button>

            {/* Date */}
            {publicationLabel && (
              <span className="absolute bottom-3 start-3 inline-flex items-center gap-1.5 rounded-full bg-slate-950/75 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
                <LuCalendarDays size={13} />
                {publicationLabel}
              </span>
            )}

            {/* Nombre de photos */}
            {images.length > 0 && (
              <span className="absolute bottom-3 end-3 inline-flex items-center gap-1 rounded-full bg-slate-950/75 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur">
                <LuImage size={13} />
                {images.length}
              </span>
            )}
          </div>

          {/* Contenu */}
          <div className="flex flex-1 flex-col p-4">
            {hasOffer && (
              <p className="text-xs font-semibold text-slate-400 line-through">
                {formatPrice(normalPrice)} TND
              </p>
            )}

            {nightlyPrice > 0 ? (<p className="lina-card-price text-xl font-black text-green-600">
              {formatPrice(nightlyPrice)}

              <span className="ms-1 text-xs font-bold">
                TND
              </span>

              <span className="ms-1 text-xs font-medium text-slate-500">
                /{" "}
                {t(
                  "details_vacation.per_night",
                  "nuit"
                )}
              </span>
            </p>) : (<p className="text-sm font-semibold text-slate-600">{t("vacationPublication.noPrice")}</p>)}

            <h3 className="mt-2 line-clamp-2 text-base font-black leading-6 text-slate-900 transition group-hover:text-green-700">
              {listing?.libelle ||
                listing?.title ||
                t(
                  "vacation.accommodation",
                  "Location de vacances"
                )}
            </h3>

            <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-500">
              <LuMapPin
                size={16}
                className="mt-0.5 shrink-0 text-green-600"
              />

              <span className="lina-card-location">
                {location ||
                  t(
                    "listing.locationUnavailable",
                    "Localisation non précisée"
                  )}
              </span>
            </p>

            {/* Caractéristiques */}
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-100 pt-3">
              {listing?.maxGuests !==
                undefined && (
                <Feature
                  icon={LuUsers}
                  value={`${
                    listing.maxGuests
                  } ${t(
                    "travelers",
                    "voyageurs"
                  )}`}
                />
              )}

              {Number(listing?.chambres) > 0 && (
                <Feature
                  icon={LuBedDouble}
                  value={`${
                    listing.chambres
                  } ${t(
                    "bedrooms",
                    "chambres"
                  )}`}
                />
              )}

              {Number(listing?.sallesBains) > 0 && (
                <Feature
                  icon={LuBath}
                  value={`${
                    listing.sallesBains
                  } ${t(
                    "bathrooms",
                    "SDB"
                  )}`}
                />
              )}
            </div>

            {/* Actions utilisateur */}
            {!isOwner &&
              !isHome &&
              !onEdit &&
              !onDelete && (
                <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
                  <button
                    type="button"
                    onClick={
                      openReservationModal
                    }
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-slate-950 px-3 text-xs font-bold text-white transition hover:bg-green-600"
                  >
                    <LuMessageSquare
                      size={15}
                    />

                    {t(
                      "details_vacation.reserve",
                      "Réserver"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleCall}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-700 transition hover:border-green-300 hover:bg-green-50 hover:text-green-700"
                  >
                    <LuPhone size={15} />

                    <span className="truncate">
                      {showPhone
                        ? (listing?.tel && <bdi dir="ltr">{listing.tel}</bdi>) ||
                          t(
                            "call",
                            "Appeler"
                          )
                        : t(
                            "call",
                            "Appeler"
                          )}
                    </span>
                  </button>
                </div>
              )}

            {/* Modifier / Supprimer */}
            {(onEdit || onDelete) && (
              <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
                {onEdit && (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 transition hover:border-green-300 hover:bg-green-50 hover:text-green-700"
                  >
                    <LuPencil size={15} />
                    {t("edit", "Modifier")}
                  </button>
                )}

                {onDelete && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 text-sm font-bold text-red-600 transition hover:bg-red-100"
                  >
                    <LuTrash2 size={15} />
                    {t(
                      "delete",
                      "Supprimer"
                    )}
                  </button>
                )}
              </div>
            )}

            {!onEdit && !onDelete && (
              <div className="mt-auto pt-4">
                <span className="inline-flex items-center gap-2 text-sm font-black text-green-600">
                  {t(
                    "listing.viewDetails",
                    "Voir le logement"
                  )}

                  <LuArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
                  />
                </span>
              </div>
            )}
          </div>
        </article>
      </li>

      {/* Modal de réservation */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeModal();
            }
          }}
        >
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            {/* En-tête */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.15em] text-green-600"> {uiI18n.t("ui.lina_immo_holidays")} </p>

                <h2 className="mt-1 text-xl font-black text-slate-950">
                  {t(
                    "details_vacation.booking_request",
                    "Demande de réservation"
                  )}
                </h2>
              </div>

              <button
                type="button"
                onClick={closeModal}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                aria-label={t(
                  "close",
                  "Fermer"
                )}
              >
                <LuX size={20} />
              </button>
            </div>

            <div className="p-5 sm:p-6">
              {/* Résumé du logement */}
              <button
                type="button"
                onClick={openListing}
                className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-start transition hover:border-green-200 hover:bg-green-50/50"
              >
                <img
                  src={imageUrl}
                  alt={
                    listing?.libelle ||
                    uiI18n.t("ui.rent")
                  }
                  className="h-24 w-28 shrink-0 rounded-xl object-cover"
                />

                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 font-black text-slate-900">
                    {listing?.libelle}
                  </h3>

                  <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                    <LuMapPin size={14} />
                    {location}
                  </p>

                  {nightlyPrice > 0 ? (<p className="mt-2 text-lg font-black text-green-600">
                    {formatPrice(
                      nightlyPrice
                    )}{" "}
                    TND
                    <span className="text-xs font-medium text-slate-500">
                      {" "} {uiI18n.t("ui.night_161")} </span>
                  </p>) : (<p className="text-sm font-semibold text-slate-600">{t("vacationPublication.noPrice")}</p>)}
                </div>
              </button>

              {loadingDetails && (
                <div className="mt-5 flex items-center justify-center rounded-2xl bg-slate-50 py-5">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-green-600" />
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="mt-6"
              >
                {/* Dates */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    label={t(
                      "details_vacation.arrival_date",
                      "Date d’arrivée"
                    )}
                  >
                    <DatePicker
                    locale={uiI18n.language.startsWith("ar") ? arTN : uiI18n.language.startsWith("en") ? enGB : fr}
                    previousMonthButtonLabel={uiI18n.t("ui.previous_month")}
                    nextMonthButtonLabel={uiI18n.t("ui.next_month")}
                      selected={startDate}
                      onChange={(date) => {
                        setStartDate(date);

                        if (
                          endDate &&
                          date &&
                          endDate <= date
                        ) {
                          setEndDate(null);
                        }
                      }}
                      excludeDateIntervals={
                        excludedDateIntervals
                      }
                      minDate={new Date()}
                      placeholderText={t(
                        "details_vacation.selectDay",
                        "Sélectionner"
                      )}
                      dateFormat="dd/MM/yyyy"
                      withPortal
                      className={inputClassName}
                    />
                  </FormField>

                  <FormField
                    label={t(
                      "details_vacation.departure_date",
                      "Date de départ"
                    )}
                  >
                    <DatePicker
                    locale={uiI18n.language.startsWith("ar") ? arTN : uiI18n.language.startsWith("en") ? enGB : fr}
                    previousMonthButtonLabel={uiI18n.t("ui.previous_month")}
                    nextMonthButtonLabel={uiI18n.t("ui.next_month")}
                      selected={endDate}
                      onChange={setEndDate}
                      excludeDateIntervals={
                        excludedDateIntervals
                      }
                      minDate={
                        startDate || new Date()
                      }
                      placeholderText={t(
                        "details_vacation.selectDay",
                        "Sélectionner"
                      )}
                      dateFormat="dd/MM/yyyy"
                      withPortal
                      className={inputClassName}
                    />
                  </FormField>
                </div>

                {/* Voyageurs */}
                <div className="mt-4">
                  <FormField
                    label={t(
                      "travelers",
                      "Voyageurs"
                    )}
                  >
                    <div className="relative">
                      <LuUsers
                        size={17}
                        className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-slate-400"
                      />

                      <input
                        type="number"
                        min="1"
                        max={
                          Number(
                            listing?.maxGuests
                          ) || 20
                        }
                        value={travelers}
                        onChange={(event) =>
                          setTravelers(
                            Math.max(
                              1,
                              Number(
                                event.target
                                  .value
                              ) || 1
                            )
                          )
                        }
                        className={`${inputClassName} ps-11`}
                      />
                    </div>
                  </FormField>
                </div>

                {/* Contact */}
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <FormField
                    label={t(
                      "details_vacation.name",
                      "Nom"
                    )}
                  >
                    <input
                      required
                      type="text"
                      value={name}
                      onChange={(event) =>
                        setName(
                          event.target.value
                        )
                      }
                      autoComplete="name"
                      className={inputClassName}
                    />
                  </FormField>

                  <FormField
                    label={t(
                      "details_vacation.email",
                      "E-mail"
                    )}
                  >
                    <input
                      required
                      type="email"
                      value={email}
                      onChange={
                        handleEmailChange
                      }
                      autoComplete="email"
                      className={inputClassName}
                    />

                    {emailError && (
                      <p className="mt-1 text-xs font-medium text-red-500">
                        {emailError}
                      </p>
                    )}
                  </FormField>

                  <FormField
                    label={t(
                      "details_vacation.phone",
                      "Téléphone"
                    )}
                  >
                    <input
                      required
                      type="tel"
                      value={phone}
                      onChange={(event) =>
                        setPhone(
                          event.target.value
                            .replace(
                              /[^0-9+\s]/g,
                              ""
                            )
                            .slice(0, 20)
                        )
                      }
                      autoComplete="tel"
                      className={inputClassName}
                    />
                  </FormField>

                  <FormField
                    label={t(
                      "details_vacation.message",
                      "Message"
                    )}
                  >
                    <textarea
                      rows={3}
                      value={message}
                      onChange={(event) =>
                        setMessage(
                          event.target.value
                        )
                      }
                      className={`${inputClassName} min-h-[96px] resize-y py-3`}
                    />
                  </FormField>
                </div>

                {/* Résumé prix */}
                {priceCalculation.durationBlocked && <p role="alert" className="text-amber-700">{uiI18n.t("ui.the_length_of_stay_discount_must_be_confirmed_before_booking")}</p>}
              {totalDays > 0 && nightlyPrice > 0 && (
                  <div className="mt-6 rounded-2xl border border-green-100 bg-green-50 p-4">
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span>
                        {formatPrice(
                          nightlyPrice
                        )}{" "} {uiI18n.t("ui.tnd")} {totalDays}{" "}
                        {t(
                          "details_vacation.nights",
                          "nuits"
                        )}
                      </span>

                      <span className="font-bold">
                        {formatPrice(
                          priceCalculation.subtotal
                        )}{" "}
                        TND
                      </span>
                    </div>

                    {priceCalculation.discountAmount >
                      0 && (
                      <div className="mt-2 flex items-center justify-between text-sm text-green-700">
                        <span>
                          {t(
                            "details_vacation.discount_applied",
                            "Réduction appliquée"
                          )}{" "}
                          (
                          {
                            priceCalculation.discountRate
                          }
                          %)
                        </span>

                        <span className="font-bold">
                          -
                          {formatPrice(
                            priceCalculation.discountAmount
                          )}{" "}
                          TND
                        </span>
                      </div>
                    )}

                    <div className="mt-4 flex items-center justify-between border-t border-green-200 pt-4">
                      <span className="font-black text-slate-900">
                        {t(
                          "details_vacation.total_price",
                          "Total estim?"
                        )}
                      </span>

                      <span className="text-xl font-black text-green-700">
                        {formatPrice(
                          priceCalculation.total
                        )}{" "}
                        TND
                      </span>
                    </div>
                  </div>
                )}

                {nightlyPrice <= 0 && <p role="status" className="mt-4 text-sm text-slate-600">{t("vacationPublication.contactForPrice")}</p>}
                <button
                  type="submit"
                  disabled={
                  nightlyPrice <= 0 || isSending ||
                    !startDate ||
                    !endDate ||
                    !name.trim() ||
                    !email.trim() ||
                    Boolean(emailError) ||
                    !phone.trim() ||
                    totalDays <= 0
                  }
                  className="
                    mt-6
                    inline-flex
                    h-12
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-2xl
                    bg-green-600
                    px-6
                    text-sm
                    font-black
                    text-white
                    shadow-lg
                    shadow-green-600/20
                    transition
                    hover:bg-green-700
                    disabled:cursor-not-allowed
                    disabled:bg-slate-300
                    disabled:shadow-none
                    focus:outline-none
                    focus:ring-4
                    focus:ring-green-500/20
                  "
                >
                  {isSending ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                      {t(
                        "details_vacation.sending",
                        "Envoi..."
                      )}
                    </>
                  ) : (
                    <>
                      <LuSend size={17} />

                      {t(
                        "details_vacation.send_reservation",
                        "Envoyer la demande"
                      )}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const inputClassName = `
  h-12
  w-full
  rounded-2xl
  border
  border-slate-200
  bg-white
  px-4
  text-sm
  font-medium
  text-slate-900
  outline-none
  transition
  placeholder:text-slate-400
  hover:border-slate-300
  focus:border-green-500
  focus:ring-4
  focus:ring-green-500/10
`;

function FormField({
  label,
  children,
}) {
  useUiTranslation();
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-black text-slate-700">
        {label}
      </span>

      {children}
    </label>
  );
}

function Feature({
  icon: Icon,
  value,
}) {
  useUiTranslation();
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
      <Icon
        size={15}
        className="shrink-0 text-green-600"
      />

      <span className="font-semibold text-slate-600">
        {value}
      </span>
    </span>
  );
}

export default memo(ListVacation);